import { useEffect, useState, type FormEvent } from "react";
import { checkInStaffTicket, lookupStaffTicket, type StaffTicket } from "../api";

export function StaffCheckIn() {
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [pin, setPin] = useState(() => window.sessionStorage.getItem("axon-staff-pin") || "");
  const [query, setQuery] = useState("");
  const [ticket, setTicket] = useState<StaffTicket | null>(null);
  const [enteringCount, setEnteringCount] = useState(1);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function lookup(currentPin = pin, currentQuery = query) {
    if (!currentPin || (!token && currentQuery.trim().length < 3)) return;
    setLoading(true);
    setError("");
    try {
      window.sessionStorage.setItem("axon-staff-pin", currentPin);
      const nextTicket = await lookupStaffTicket(currentPin, token ? { token } : { query: currentQuery.trim() });
      setTicket(nextTicket);
      setEnteringCount(Math.min(1, nextTicket.remainingEntries));
    } catch (caught) {
      setTicket(null);
      setError(caught instanceof Error ? caught.message : "Ticket konnte nicht gefunden werden.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (token && pin) void lookup(pin, "");
    // The QR token is fixed for the mounted page; lookup only after a remembered PIN is available.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    await lookup();
  }

  async function checkIn() {
    if (!ticket) return;
    setLoading(true);
    setError("");
    try {
      const result = await checkInStaffTicket(pin, ticket.id, enteringCount);
      setTicket({
        ...ticket,
        status: result.status || ticket.status,
        checkedInAt: result.checkedInAt || ticket.checkedInAt || new Date().toISOString(),
        checkedInCount: result.checkedInCount ?? ticket.checkedInCount + enteringCount,
        remainingEntries: result.remainingEntries ?? ticket.remainingEntries - enteringCount,
        lastCheckedInAt: result.lastCheckedInAt || new Date().toISOString(),
      });
      setEnteringCount(1);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Check-in fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="staff-page">
      <section className="staff-panel">
        <p className="eyebrow">AXON · DOOR CONTROL</p>
        <h1>Guest check-in</h1>
        <form className="staff-search" onSubmit={submit}>
          <label>
            <span>Staff-PIN</span>
            <input type="password" inputMode="numeric" autoComplete="current-password" value={pin} onChange={(event) => setPin(event.target.value)} required />
          </label>
          {!token && <label><span>8-stelliger Code, Name oder E-Mail</span><input value={query} onChange={(event) => setQuery(event.target.value)} minLength={3} required /></label>}
          <button className="button button--pink" disabled={loading} type="submit">{loading ? "Prüfe …" : token ? "QR prüfen" : "Ticket suchen"}</button>
        </form>
        {error && <p className="staff-message staff-message--error" role="alert">{error}</p>}
        {ticket && (
          <article className={`staff-ticket staff-ticket--${ticket.status}`}>
            <p className="eyebrow">{ticket.status === "checked_in" ? "BEREITS EINGELASSEN" : ticket.status === "revoked" ? "UNGÜLTIG" : "GÜLTIG"}</p>
            <h2>{ticket.name}</h2>
            <dl>
              <div><dt>Event</dt><dd>{ticket.eventName}</dd></div>
              <div><dt>Code</dt><dd>{ticket.entryCode}</dd></div>
              <div><dt>E-Mail</dt><dd>{ticket.email}</dd></div>
              <div><dt>Gebucht</dt><dd>{ticket.partySize} {ticket.partySize === 1 ? "Person" : "Personen"}</dd></div>
              <div><dt>Eingelassen</dt><dd>{ticket.checkedInCount} von {ticket.partySize}</dd></div>
              <div><dt>Offen</dt><dd>{ticket.remainingEntries} {ticket.remainingEntries === 1 ? "Eintritt" : "Eintritte"}</dd></div>
              {ticket.checkedInAt && <div><dt>Check-in</dt><dd>{new Date(ticket.checkedInAt).toLocaleString("de-DE")}</dd></div>}
            </dl>
            {ticket.status === "issued" && (
              <>
                <label>
                  <span>Jetzt einlassen</span>
                  <select value={enteringCount} onChange={(event) => setEnteringCount(Number(event.target.value))}>
                    {Array.from({ length: ticket.remainingEntries }, (_, index) => index + 1).map((count) => <option key={count} value={count}>{count} {count === 1 ? "Person" : "Personen"}</option>)}
                  </select>
                </label>
                <button className="button button--dark" type="button" disabled={loading || ticket.remainingEntries < 1} onClick={checkIn}>
                  {enteringCount} {enteringCount === 1 ? "Person" : "Personen"} einlassen · {(enteringCount * ticket.admissionPrice).toFixed(2).replace(".", ",")} €
                </button>
              </>
            )}
          </article>
        )}
      </section>
    </main>
  );
}
